import { ResearchReport } from '@/types/research';

/**
 * Returns the date 3 days ago as an ISO string, so the demo report
 * always looks recently generated rather than showing a stale date.
 */
function recentDate(): string {
  const d = new Date();
  d.setDate(d.getDate() - 3);
  return d.toISOString();
}

/**
 * Hardcoded NVIDIA research report shown to free and unauthenticated users
 * as a demo of the Research tab feature.
 *
 * generatedAt is computed dynamically so the report never looks stale.
 */
export function getNvidiaDemoReport(): ResearchReport {
  return {
    symbol: 'NVDA',
    generatedAt: recentDate(),

    analystRating: {
      consensus: 'strong_buy',
      confidence: 90,
      reasoning:
        'NVIDIA dominates the AI accelerator market with its Blackwell GPU platform now ramping at scale. Record data center revenue, expanding software moat via CUDA/NIM, and a multi-year AI infrastructure buildout underpin a strong outlook.',
    },

    keyMetrics: {
      pe: 42.8,
      forwardPe: 28.5,
      eps: 3.28,
      dividendYield: 0.0002,
      beta: 1.64,
      fiftyTwoWeekLow: 86.62,
      fiftyTwoWeekHigh: 195.95,
      priceToBook: 44.6,
      debtToEquity: 13.47,
      revenueGrowth: 0.78,
      profitMargin: 0.569,
    },

    macroFactors: [
      {
        factor: 'AI Infrastructure Spending',
        impact: 'positive',
        description:
          'Hyperscale cloud providers are projecting record-high capex for 2026, with AI infrastructure as the top priority — directly benefiting NVIDIA\'s data center GPU sales.',
      },
      {
        factor: 'US-China Trade Policy',
        impact: 'negative',
        description:
          'Expanded export restrictions on advanced AI chips to China continue to limit NVIDIA\'s addressable market in one of the largest GPU markets globally.',
      },
      {
        factor: 'Federal Reserve Rate Trajectory',
        impact: 'positive',
        description:
          'The Fed\'s dovish pivot with rate cuts underway is supportive of growth stock valuations and eases cost of capital for NVIDIA\'s enterprise customers.',
      },
      {
        factor: 'Enterprise AI Adoption',
        impact: 'positive',
        description:
          'Enterprise adoption of generative AI has moved from pilot to production at scale, driving sustained demand for NVIDIA\'s Blackwell and Hopper GPU platforms.',
      },
    ],

    earningsInfo: {
      nextDate: '2026-03-26',
      recentHighlights: [
        'Q3 FY2026 revenue of $41.2B, up 82% year-over-year, beating estimates by $1.6B',
        'Data center revenue reached $37.9B, up 88% year-over-year driven by Blackwell ramp',
        'Gross margins expanded to 74.5%, reflecting pricing power on Blackwell GPUs',
        'Management guided Q4 FY2026 revenue of $44.0B ± 2%, above consensus of $41.8B',
      ],
      links: [],
    },

    technicalAnalysis: {
      support: 120.0,
      resistance: 175.0,
      trend: 'bullish',
      signals: [
        '50-day moving average trending above 200-day MA — golden cross intact',
        'RSI at 64 — moderately bullish, room to run before overbought',
        'Strong accumulation pattern with institutional buying on pullbacks to $125-130 zone',
        'MACD histogram positive and expanding, confirming upward momentum',
      ],
    },

    riskFactors: [
      'Premium valuation (43x trailing P/E) leaves limited margin of safety if growth decelerates',
      'Customer concentration risk — top 4 hyperscalers represent over 40% of data center revenue',
      'Intensifying competition from AMD MI350X, Intel Falcon Shores, and custom ASICs (Google TPU v6, Amazon Trainium 2)',
      'Export controls could tighten further, restricting sales to additional regions beyond China',
      'Potential cyclicality in data center capex if enterprise AI ROI expectations are not met',
    ],

    opportunities: [
      'Blackwell Ultra (B300) platform on track for H2 2026, offering another generational performance leap',
      'NVIDIA software ecosystem (CUDA, NIM microservices, AI Enterprise) now generating $1B+ annual recurring revenue',
      'Automotive and robotics (DRIVE Thor, Isaac) revenue inflecting with production design wins at major OEMs',
      'Sovereign AI initiatives across 30+ countries driving new government-backed GPU cluster deployments',
      'Inference market now larger than training — NVIDIA\'s TensorRT and NIM are the de facto standard for production AI serving',
    ],

    summary: `**NVIDIA (NVDA) continues to dominate the AI acceleration market**, with its data center segment delivering 88% year-over-year growth to $37.9B in Q3 FY2026 — driven by the Blackwell GPU ramp. The company's CUDA software ecosystem, now complemented by NIM microservices generating over $1B in recurring revenue, has created a competitive moat that rivals have been unable to breach. Gross margins expanded to 74.5%, reflecting both pricing power and operating leverage.

**The Blackwell product cycle is proving to be NVIDIA's strongest yet**, with the B200 and GB200 NVL72 systems seeing extraordinary demand from hyperscalers, enterprises, and sovereign AI projects. Management's Q4 FY2026 guidance of $44B significantly beat consensus, signaling continued demand strength. The upcoming Blackwell Ultra (B300) platform, expected in H2 2026, should extend this product supercycle.

**Investors should be aware of valuation risk at 43x trailing earnings**, though the forward P/E of 28.5x is more reasonable given the growth trajectory. Key concerns include customer concentration among hyperscalers and rising competition from AMD and custom silicon. On balance, NVIDIA's technology leadership, software moat, and expanding TAM across data center, automotive, and robotics support a Strong Buy thesis with a 12-month price target range of $175-$195.`,

    disclaimer:
      'This is a static demo report for illustrative purposes only. Data may not reflect current market conditions. This is not investment advice. Always consult a licensed financial professional before making investment decisions.',
  };
}
